import React from 'react'
import { Card, Progress, Rate, Tag } from 'antd'
import { applyLogoFallbackStyle, markLogoLikeImageIfNeeded } from '../../utils/logoCover'

type HomeBookCardProps = {
  id: number
  title: string
  author: string
  cover: string
  format?: string
  avgRating?: number
  ratingCount?: number
  readProgressPercent?: number
  badgeText?: string
  tagText?: string
  description?: string
  onClick: () => void
}

/**
 * 首页复用书籍卡片。
 */
const HomeBookCard: React.FC<HomeBookCardProps> = ({
  id,
  title,
  author,
  cover,
  format,
  avgRating,
  ratingCount,
  readProgressPercent,
  badgeText,
  tagText,
  description,
  onClick,
}) => {
  return (
    <Card
      hoverable
      styles={{ body: { padding: 12 } }}
      className="h-full border-none shadow-sm hover:shadow-theme-md transition-all duration-300 rounded-lg overflow-hidden group"
      onClick={onClick}
      cover={(
        <div className="relative w-full overflow-hidden bg-gray-100" style={{ aspectRatio: '3 / 4' }}>
          <img
            src={cover}
            alt={title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onLoad={(event) => {
              const img = event.currentTarget
              void markLogoLikeImageIfNeeded(img)
            }}
            onError={(event) => {
              const img = event.currentTarget
              if (img.dataset.fallbackApplied === '1') return
              img.dataset.fallbackApplied = '1'
              img.src = '/icons/My_logo.png'
              applyLogoFallbackStyle(img)
            }}
          />
          {badgeText ? (
            <div className="absolute top-2 left-2">
              <span className="inline-flex items-center justify-center text-xs font-semibold text-white bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded shadow-sm">
                {badgeText}
              </span>
            </div>
          ) : null}
          {tagText ? (
            <div className="absolute top-2 right-2">
              <Tag color="#ef4444" style={{ marginInlineEnd: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: 'none' }}>
                {tagText}
              </Tag>
            </div>
          ) : null}
        </div>
      )}
    >
      <div className="text-gray-800" style={{ fontWeight: 600, lineHeight: 1.4, marginBottom: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '2.8em' }}>
        {title}
      </div>
      <div className="text-xs text-gray-500" style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {author || '未知作者'}
      </div>
      <div className="text-[11px] text-gray-400 mt-0.5">ID: {id}</div>
      {format ? (
        <div className="text-xs text-primary-600 mt-1 bg-primary-50 inline-block px-1.5 py-0.5 rounded">{format}</div>
      ) : null}
      {typeof avgRating === 'number' && avgRating > 0 ? (
        <div className="mt-2 flex items-center gap-1">
          <Rate disabled allowHalf value={Number(avgRating || 0)} style={{ fontSize: 12, color: '#f59e0b' }} />
          <div className="text-xs text-gray-400 transform scale-90 origin-left">{Number(ratingCount || 0)}人</div>
        </div>
      ) : null}
      {typeof readProgressPercent === 'number' && readProgressPercent > 0 ? (
        <div className="mt-2">
          <div className="mb-1 text-[11px] text-gray-500">阅读进度 {Math.round(readProgressPercent)}%</div>
          <Progress percent={Math.max(0, Math.min(100, Number(readProgressPercent || 0)))} size="small" showInfo={false} strokeColor="#f59e0b" />
        </div>
      ) : null}
      {description ? (
        <div className="mt-2 text-[12px] text-gray-500" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '3em' }}>
          {description}
        </div>
      ) : null}
    </Card>
  )
}

export default HomeBookCard
